import { Checklist } from "@/bryntum-lib/models/Checklist";
import { Badge } from "@/modern/components/ui/badge";
import { Dialog } from "@/modern/components/ui/dialog";
import { DialogTrigger } from "@/modern/components/ui/dialog-shadcn";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import * as Editable from "@/modern/components/ui/editable";
import { ChecklistAssignedUsers } from "@/react/components/entityViews/checklist/components/ChecklistAssignedUsers";
import { ChecklistProgressBar } from "@/react/components/entityViews/checklist/components/ChecklistProgressBar";
import { ChecklistStats } from "@/react/components/entityViews/checklist/components/ChecklistStats";
import { cn } from "@/modern/utils";
import { ChecklistAddItemsModal } from "@/react/components/entityViews/checklist/components/editors/ChecklistAddItemsModal";
import { ChecklistDetailModalHeaderAndBody } from "@/react/components/entityViews/checklist/components/editors/ChecklistDetailModal";
import { usePinnedChecklists } from "@/react/hooks/usePinnedChecklists";
import { Archive, Edit2Icon, EyeIcon, MoreVertical, PinIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { SharedModalBody, SharedModal, SharedModalHeader } from "@/modern/components/shared-modal-component";
import { useEntityChecklistContext } from "@/react/components/entityViews/checklist/context/EntityChecklistContext";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";

interface ChecklistRowContentProps {
  checklist: Checklist;
  showAsHeader?: boolean;
  onSelect?: () => void;
  handleListView: () => void;
}

export const ChecklistRowComponent = ({ checklist, showAsHeader = false, onSelect, handleListView }: ChecklistRowContentProps) => {
  const { updateChecklist } = useEntityChecklistContext();
  const [isEditingChecklistName, setIsEditingChecklistName] = useState(false);

  const toggleEditChecklistName = () => {
    setIsEditingChecklistName(!isEditingChecklistName);
  };

  const { isPinnedChecklist } = usePinnedChecklists();
  const isPinned = isPinnedChecklist(+checklist.id);
  return (
    <div className=" min-w-0 flex-1 ">
      {/* Title and Stats Row */}
      <div className="flex items-center justify-between gap-2 ">
        <div className="flex items-center gap-2 flex-1 min-w-0 ">
          <Editable.Root editing={isEditingChecklistName} defaultValue={checklist.title} onSubmit={(value) => updateChecklist(checklist, { title: value })}>
            <Editable.Area className="flex items-center gap-2">
              <AnimatePresence>
                {isPinned && (
                  <motion.div initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: "auto" }} exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }}>
                    <PinIcon className="size-3.5" />
                  </motion.div>
                )}
              </AnimatePresence>
              <Editable.Input className="text-base md:text-base  flex-1 cursor-pointer hover:text-foreground" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={onSelect} className="flex gap-2 items-center min-w-0 tuncate">
                    <Editable.Preview
                      className={cn(
                        "text-base md:text-base  truncate flex-1 cursor-pointer hover:decoration-foreground transition-colors",
                        showAsHeader ? " text-foreground " : " underline decoration-muted-foreground/80 decoration-from-font underline-offset-[3px]"
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{checklist.title}</p>
                </TooltipContent>
              </Tooltip>
            </Editable.Area>
          </Editable.Root>
          {/* <h3 className={cn(" text-base truncate flex-1 cursor-pointer hover:text-foreground", showAsHeader ? " text-foreground " : "underline")}>{checklist.title}</h3> */}
          {checklist.archived && (
            <Badge variant="muted" size="xs">
              <Archive className="h-3 w-3" />
            </Badge>
          )}
        </div>

        {/* Right Section - Icons, Users, Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Icon Stats */}
          <ChecklistStats imageItemsCount={checklist.imageItemsCount} commentItemsCount={checklist.commentItemsCount} nudgeDate={checklist.nudgeDate} />

          {/* Assigned Users */}
          <ChecklistAssignedUsers assignedBoardUsers={checklist.assignedBoardUsers} />
          {/* Action Buttons */}
          <div className="flex items-center gap-1 ">
            <ChecklistRowMoreMenu toggleEditChecklistName={toggleEditChecklistName} checklist={checklist} onSelect={onSelect} handleListView={handleListView} />
          </div>
        </div>
      </div>

      {/* Progress Bar and Stats */}
      <ChecklistProgressBar checklistId={checklist.id} />
    </div>
  );
};

// doesn't use the more menu because of the nested dialogs in the more menu -- ended up being circular
const ChecklistRowMoreMenu = ({ checklist }: { checklist: Checklist; onSelect?: () => void; handleListView: () => void; toggleEditChecklistName: () => void }) => {
  const [open, setOpen] = useState(false);
  const [modalContent, setModalContent] = useState<"view" | "edit" | null>(null);

  const { togglePinChecklist, isPinnedChecklist } = usePinnedChecklists();
  const isPinned = isPinnedChecklist(+checklist.id);

  return (
    <Dialog modal={true}>
      <div className=" flex justify-end items-start">
        <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-lg" className="">
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className=" p-1">
            <DropdownMenuItem className="justify-start" onMouseDown={() => togglePinChecklist(+checklist.id)}>
              <PinIcon className="size-4 mr-1" />
              {isPinned ? "Unpin checklist" : "Pin checklist"}
            </DropdownMenuItem>
            <DialogTrigger asChild>
              <DropdownMenuItem className="justify-start" onClick={() => setModalContent("edit")}>
                <Edit2Icon className="size-4 mr-1" />
                <span>Edit checklist</span>
              </DropdownMenuItem>
            </DialogTrigger>
            <DialogTrigger asChild>
              <DropdownMenuItem className="justify-start" onClick={() => setModalContent("view")}>
                <EyeIcon className="size-4 mr-1" />
                <span>View checklist</span>
              </DropdownMenuItem>
            </DialogTrigger>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <SharedModal className="md:max-w-2xl">
        {modalContent === "view" && <ChecklistDetailModalHeaderAndBody checklist={checklist} />}
        {modalContent === "edit" && (
          <>
            <SharedModalHeader>Edit checklist</SharedModalHeader>
            <SharedModalBody>
              <ChecklistAddItemsModal checklist={checklist} />
            </SharedModalBody>
          </>
        )}
      </SharedModal>
    </Dialog>
  );
};
